'use client'
import * as React from 'react'
import {
  ArrowRight,
  Check,
  Loader2,
  AlertCircle,
  Lock,
  Mail,
  RotateCcw,
  ShieldCheck,
  Minus,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ToolProgress } from '@/components/tools/tool-progress'
import { cn } from '@/lib/utils'
import { AI_SIGNAL_BY_ID, type AiSignalId } from '@/lib/copy/ai-readiness-signals'

// The serverless API base. idukki-web reaches the public serverless API
// directly from the client (see components/tools/ugc-roi-calculator.tsx,
// which uses the same `https://api.idukki.io` base). Overridable per
// environment so dev/staging point at their own deploy.
const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'https://api.idukki.io'

type CheckStatus = 'pass' | 'warn' | 'fail'

type ScanCheck = {
  id: string
  label: string
  status: CheckStatus
  detail: string
}

type ScanResult = {
  url: string
  scannedAt: string
  score: number
  platform: string | null
  checks: ScanCheck[]
}

// The three checks shown for free; the remaining four sit behind the
// email gate. Order matches the API contract check ids.
const TEASER_IDS = ['crawlers', 'llmsTxt', 'structuredData'] as const
const LOCKED_IDS = ['productFeed', 'sitemap', 'metadata', 'faq'] as const

// Friendly fallback labels so the locked rows read as a real report even
// before the email unlock returns live detail from the API.
const LOCKED_LABELS: Record<(typeof LOCKED_IDS)[number], string> = {
  productFeed: 'Product feed an agent can read',
  sitemap: 'Sitemap + clean URL structure',
  metadata: 'Title + meta description AI can quote',
  faq: 'FAQ / Q&A marked up for answers',
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function bandForScore(score: number): { tone: 'green' | 'amber' | 'red'; label: string } {
  if (score >= 70) return { tone: 'green', label: 'Findable' }
  if (score >= 40) return { tone: 'amber', label: 'Partly findable' }
  return { tone: 'red', label: 'Hard to find' }
}

// Score-dial colour is committed to the brand on a strong result (lime),
// amber for the middle, destructive red for a weak one. All via tokens.
function dialStroke(tone: 'green' | 'amber' | 'red'): string {
  if (tone === 'green') return 'hsl(var(--brand-lime))'
  if (tone === 'amber') return 'hsl(38 92% 50%)'
  return 'hsl(var(--destructive))'
}

function StatusChip({ status }: { status: CheckStatus }) {
  const map: Record<CheckStatus, { cls: string; icon: React.ReactNode; label: string }> = {
    pass: {
      cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      icon: <Check className="h-3.5 w-3.5" />,
      label: 'Pass',
    },
    warn: {
      cls: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
      icon: <Minus className="h-3.5 w-3.5" />,
      label: 'Partial',
    },
    fail: {
      cls: 'border-destructive/30 bg-destructive/10 text-destructive',
      icon: <X className="h-3.5 w-3.5" />,
      label: 'Fail',
    },
  }
  const s = map[status]
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        s.cls,
      )}
    >
      {s.icon}
      {s.label}
    </span>
  )
}

function CheckRow({ check }: { check: ScanCheck }) {
  // Every failing / partial signal carries an inline "Fix with Idukki" CTA that
  // deep-links to the remediation for that exact signal (shared registry, so it
  // matches the static scorecard on /ai-readiness). Passing rows stay quiet.
  const fix = check.status !== 'pass' ? AI_SIGNAL_BY_ID[check.id as AiSignalId] : undefined
  return (
    <li className="flex items-start gap-3 py-3.5">
      <StatusChip status={check.status} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{check.label}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground text-pretty">{check.detail}</p>
        {fix && (
          <a
            href={fix.fixHref}
            className="group mt-1.5 inline-flex items-center gap-1 text-[13px] font-semibold text-primary transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-[hsl(var(--accent-foreground))] focus-visible:outline-none focus-visible:[box-shadow:var(--ring)]"
          >
            Fix with Idukki
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)] group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden />
          </a>
        )}
      </div>
    </li>
  )
}

export function AiReadinessScan() {
  const [url, setUrl] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [result, setResult] = React.useState<ScanResult | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  // Email gate state for the locked four checks.
  const [email, setEmail] = React.useState('')
  const [unlocked, setUnlocked] = React.useState(false)
  const [leadState, setLeadState] = React.useState<'idle' | 'submitting' | 'error'>('idle')
  const [leadError, setLeadError] = React.useState<string | null>(null)

  async function onScan(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return
    setLoading(true)
    setError(null)
    setResult(null)
    setUnlocked(false)
    setEmail('')
    setLeadState('idle')
    setLeadError(null)

    // Hold the staged progress for a real-feeling minimum so the readout
    // never flashes; the scan itself is the source of truth.
    const minWait = new Promise((r) => setTimeout(r, 2600))
    try {
      const req = fetch(`${API_BASE}/aeo-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed }),
        signal: AbortSignal.timeout(20_000),
      })
      const [res] = await Promise.all([req, minWait])
      if (res.status === 404) {
        setError('The scanner is warming up and is not live yet. Check back shortly, or start free with Idukki and we will run it for you.')
        return
      }
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.result) {
        setError(data?.message || 'That scan did not complete. Check the URL and try again.')
        return
      }
      setResult(data.result as ScanResult)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'TimeoutError') {
        setError('The scan timed out. Some stores block automated requests; try the homepage URL.')
      } else {
        setError('Network error reaching the scanner. Try again in a moment.')
      }
    } finally {
      setLoading(false)
    }
  }

  async function onUnlock(e: React.FormEvent) {
    e.preventDefault()
    if (!result) return
    if (!EMAIL_RE.test(email.trim())) {
      setLeadError('Enter a valid email so we can send the full report.')
      return
    }
    setLeadState('submitting')
    setLeadError(null)
    try {
      const res = await fetch(`${API_BASE}/aeo-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          url: result.url,
          score: result.score,
          platform: result.platform,
        }),
        signal: AbortSignal.timeout(15_000),
      })
      // The reveal is the value exchange; unlock even if the lead store is
      // briefly unavailable so a visitor who gave a real email is never
      // punished for our backend. The email was still attempted.
      if (res.status === 404) {
        setUnlocked(true)
        return
      }
      const data = await res.json().catch(() => null)
      if (!res.ok || data?.status === false) {
        setLeadState('error')
        setLeadError('We could not save that just now. Your report is unlocked below anyway.')
        setUnlocked(true)
        return
      }
      setUnlocked(true)
    } catch {
      setLeadState('error')
      setLeadError('Network hiccup. Your report is unlocked below anyway.')
      setUnlocked(true)
    } finally {
      if (leadState !== 'error') setLeadState('idle')
    }
  }

  function reset() {
    setResult(null)
    setError(null)
    setUrl('')
    setUnlocked(false)
    setEmail('')
  }

  const teaser = result ? TEASER_IDS.map((id) => result.checks.find((c) => c.id === id)).filter(Boolean) as ScanCheck[] : []
  const locked = result
    ? LOCKED_IDS.map(
        (id) =>
          result.checks.find((c) => c.id === id) ?? {
            id,
            label: LOCKED_LABELS[id],
            status: 'warn' as CheckStatus,
            detail: 'Unlock the full report to see this result.',
          },
      )
    : []

  // ---- Hero input (no scan yet) ----
  if (!result && !loading) {
    return (
      <div className="w-full max-w-xl">
        <form onSubmit={onScan} className="flex flex-col gap-3 sm:flex-row">
          <Input
            type="text"
            inputMode="url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="yourstore.com"
            aria-label="Your store URL"
            className="h-12 flex-1 border-white/20 bg-white/10 text-base text-white placeholder:text-white/50 focus-visible:ring-primary"
          />
          <Button
            type="submit"
            size="lg"
            className="h-12 bg-brand-lime px-6 font-semibold text-brand-navy hover:bg-brand-lime/90 focus-visible:ring-2 focus-visible:ring-white"
          >
            Run the scan <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </form>
        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-white/15 bg-white/5 p-3 text-sm text-white/90">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />
            <span>{error}</span>
          </div>
        )}
        <p className="mt-4 text-sm text-white/60">
          Checks the same signals ChatGPT, Claude, Perplexity and Google&rsquo;s AI use.
        </p>
      </div>
    )
  }

  // ---- Scanning ----
  if (loading) {
    return (
      <div className="w-full max-w-xl">
        <ToolProgress
          label={url.trim() || 'your store'}
          steps={[
            { title: 'Fetching your store', sub: 'Homepage, robots.txt and llms.txt' },
            { title: 'Reading AI-readiness signals', sub: 'Crawler access, structured data, feed' },
            { title: 'Scoring', sub: '7 checks, red / amber / green' },
          ]}
          stepMs={850}
        />
      </div>
    )
  }

  // ---- Result scorecard ----
  const band = bandForScore(result!.score)
  const circumference = 2 * Math.PI * 52
  const dash = (result!.score / 100) * circumference

  return (
    <div className="w-full max-w-xl">
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] shadow-2xl backdrop-blur">
        {/* Score header */}
        <div className="flex flex-col items-center gap-6 border-b border-white/10 p-6 sm:flex-row sm:items-center sm:gap-7 sm:p-8">
          {/* Dial */}
          <div className="relative h-32 w-32 shrink-0">
            <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" aria-hidden>
              <circle cx="60" cy="60" r="52" fill="none" stroke="hsl(0 0% 100% / 0.12)" strokeWidth="8" />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke={dialStroke(band.tone)}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circumference}`}
                className="ai-dial-arc"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-4xl font-bold tabular-nums text-white">{result!.score}</span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/50">/ 100</span>
            </div>
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-lime">AI readiness</p>
            <p className="mt-1.5 font-display text-2xl font-semibold text-white">{band.label}</p>
            <p className="mt-1 truncate text-sm text-white/60" title={result!.url}>
              {result!.url}
              {result!.platform ? <span className="text-white/40"> · {result!.platform}</span> : null}
            </p>
          </div>
        </div>

        {/* Teaser rows */}
        <div className="px-6 sm:px-8">
          <ul className="divide-y divide-white/10 [&_p]:text-white/70 [&_.text-foreground]:text-white">
            {teaser.map((c) => (
              <CheckRow key={c.id} check={c} />
            ))}
          </ul>
        </div>

        {/* Locked rows */}
        <div className="relative px-6 pb-6 sm:px-8 sm:pb-8">
          <ul
            className={cn(
              'divide-y divide-white/10 [&_p]:text-white/70 [&_.text-foreground]:text-white',
              !unlocked && 'pointer-events-none select-none blur-sm',
            )}
            aria-hidden={!unlocked}
          >
            {locked.map((c) => (
              <CheckRow key={c.id} check={c} />
            ))}
          </ul>

          {!unlocked && (
            <div className="absolute inset-x-4 bottom-4 top-4 flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-brand-navy/80 p-5 text-center backdrop-blur-sm sm:inset-x-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-lime/15 text-brand-lime">
                <Lock className="h-4 w-4" />
              </span>
              <p className="font-display text-lg font-medium text-white">Get your full 7-point report</p>
              <p className="max-w-xs text-sm text-white/60 text-pretty">
                The remaining four checks (feed, sitemap, metadata, Q&amp;A) plus the fixes, sent to your inbox.
              </p>
              <form onSubmit={onUnlock} className="mt-1 flex w-full max-w-sm flex-col gap-2 sm:flex-row">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourstore.com"
                  aria-label="Email for the full report"
                  className="h-11 flex-1 border-white/20 bg-white/10 text-white placeholder:text-white/40 focus-visible:ring-primary"
                />
                <Button
                  type="submit"
                  disabled={leadState === 'submitting'}
                  className="h-11 bg-brand-lime font-semibold text-brand-navy hover:bg-brand-lime/90 focus-visible:ring-2 focus-visible:ring-white"
                >
                  {leadState === 'submitting' ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Unlocking
                    </>
                  ) : (
                    <>
                      <Mail className="mr-1.5 h-4 w-4" /> Unlock
                    </>
                  )}
                </Button>
              </form>
              {leadError && <p className="text-xs text-white/70">{leadError}</p>}
              <p className="text-[11px] text-white/40">No spam. One report, optional weekly re-run. Unsubscribe anytime.</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
        <Button
          type="button"
          variant="outline"
          onClick={reset}
          className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
        >
          <RotateCcw className="mr-1.5 h-4 w-4" /> Scan another store
        </Button>
        <Button asChild className="bg-brand-lime font-semibold text-brand-navy hover:bg-brand-lime/90 focus-visible:ring-2 focus-visible:ring-white">
          <a href="https://app.idukki.io/register?source=ai-readiness">
            Fix it free with Idukki <ArrowRight className="ml-1.5 h-4 w-4" />
          </a>
        </Button>
      </div>
      {unlocked && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/60">
          <ShieldCheck className="h-4 w-4 text-brand-lime" /> Full report unlocked. A copy is on its way to your inbox.
        </p>
      )}

      <style>{`
        .ai-dial-arc { transition: stroke-dasharray 700ms cubic-bezier(0.22, 1, 0.36, 1); }
        @media (prefers-reduced-motion: reduce) { .ai-dial-arc { transition: none; } }
      `}</style>
    </div>
  )
}
